/**
 * Blog tag taxonomy shared by the SPA, the tag catalog generator, sitemap and prerender.
 *
 * Raw tags come from two sources (static blog catalogs + Supabase `blog_posts.tags`) in mixed
 * case, language and spelling. Every raw tag is folded to one canonical slug:
 *   1. normalizeBlogTagSlug: lowercase, Turkish characters → ASCII, non-alphanumerics → "-"
 *   2. BLOG_TAG_ALIASES: clearly identical concepts (translation pairs, spelling variants) share one slug
 * so near-duplicate listing pages cannot exist.
 */

/** A tag listing page is indexable only when this many posts carry the tag. */
export const BLOG_TAG_MIN_INDEXABLE_POSTS = 2;

export const BLOG_TAG_LOCALES = ['tr', 'en'];

export function normalizeBlogTagSlug(tag) {
  return String(tag ?? '')
    .trim()
    .toLowerCase()
    .replace(/ı/g, 'i')
    .replace(/ğ/g, 'g')
    .replace(/ü/g, 'u')
    .replace(/ş/g, 's')
    .replace(/ö/g, 'o')
    .replace(/ç/g, 'c')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Normalized alias slug → canonical slug. Only exact synonyms belong here; related-but-different
 * concepts (e.g. "MVP" vs "MVP development company") stay separate tags.
 */
export const BLOG_TAG_ALIASES = {
  'chatgpt-entegrasyonu': 'chatgpt-integration',
  'yapay-zeka-yazilim-sirketi': 'ai-development-company',
  'ux-design': 'ux',
  'ux-tasarimi': 'ux',
  'yapay-zeka': 'ai',
  'artificial-intelligence': 'ai',
  'llm-entegrasyonu': 'llm-integration',
  mobil: 'mobile',
  performans: 'performance',
  guvenlik: 'security',
  mimari: 'architecture',
  'software-architecture': 'architecture',
  gozlemlenebilirlik: 'observability',
  'technical-debt': 'tech-debt',
  'teknik-borc': 'tech-debt',
  'node-js': 'nodejs',
  reactjs: 'react',
  'react-js': 'react',
  'multi-tenant': 'multi-tenancy',
  'dagitik-sistemler': 'distributed-systems',
};

export function canonicalBlogTagSlug(tag) {
  const slug = normalizeBlogTagSlug(tag);
  return BLOG_TAG_ALIASES[slug] ?? slug;
}

/** Display labels per locale; tags missing here fall back to their most common raw spelling. */
export const BLOG_TAG_LABELS = {
  ai: { tr: 'Yapay zeka (AI)', en: 'AI' },
  'ai-assistant': { tr: 'AI asistanı', en: 'AI assistant' },
  'ai-development-company': { tr: 'Yapay zeka yazılım şirketi', en: 'AI development company' },
  'ai-product-development': { tr: 'AI ürün geliştirme', en: 'AI product development' },
  'api-design': { tr: 'API tasarımı', en: 'API design' },
  architecture: { tr: 'Yazılım mimarisi', en: 'Software architecture' },
  backend: { tr: 'Backend', en: 'Backend' },
  canary: { tr: 'Canary release', en: 'Canary releases' },
  'chatgpt-integration': { tr: 'ChatGPT entegrasyonu', en: 'ChatGPT integration' },
  cloud: { tr: 'Bulut', en: 'Cloud' },
  compatibility: { tr: 'Geriye dönük uyumluluk', en: 'Backward compatibility' },
  'conflict-resolution': { tr: 'Çatışma çözümü', en: 'Conflict resolution' },
  'custom-software-development-company': {
    tr: 'Özel yazılım geliştirme şirketi',
    en: 'Custom software development company',
  },
  delivery: { tr: 'Yazılım teslimatı', en: 'Software delivery' },
  deprecation: { tr: 'Deprecation yönetimi', en: 'Deprecation' },
  'developer-experience': { tr: 'Geliştirici deneyimi', en: 'Developer experience' },
  'distributed-systems': { tr: 'Dağıtık sistemler', en: 'Distributed systems' },
  drift: { tr: 'Model drift', en: 'Model drift' },
  edge: { tr: 'Edge computing', en: 'Edge computing' },
  engineering: { tr: 'Yazılım mühendisliği', en: 'Software engineering' },
  evaluation: { tr: 'AI değerlendirme (evaluation)', en: 'AI evaluation' },
  'event-loop': { tr: 'Event loop', en: 'Event loop' },
  frontend: { tr: 'Frontend', en: 'Frontend' },
  governance: { tr: 'AI yönetişimi', en: 'AI governance' },
  'ios-android-gelistirme': { tr: 'iOS ve Android geliştirme', en: 'iOS and Android development' },
  jsi: { tr: 'JSI', en: 'JSI' },
  latency: { tr: 'Gecikme (latency)', en: 'Latency' },
  leadership: { tr: 'Mühendislik liderliği', en: 'Engineering leadership' },
  llm: { tr: 'LLM', en: 'LLM' },
  'llm-integration': { tr: 'LLM entegrasyonu', en: 'LLM integration' },
  mobile: { tr: 'Mobil', en: 'Mobile' },
  'mobil-uygulama-gelistirme': { tr: 'Mobil uygulama geliştirme', en: 'Mobile app development' },
  'mobile-app-development-company': {
    tr: 'Mobil uygulama geliştirme şirketi',
    en: 'Mobile app development company',
  },
  'mobile-app-development-cost': { tr: 'Mobil uygulama maliyeti', en: 'Mobile app development cost' },
  'multi-tenancy': { tr: 'Çoklu kiracı (multi-tenancy)', en: 'Multi-tenancy' },
  mvp: { tr: 'MVP', en: 'MVP' },
  'mvp-development-company': { tr: 'MVP geliştirme şirketi', en: 'MVP development company' },
  'mvp-gelistirme': { tr: 'MVP geliştirme', en: 'MVP development' },
  'mvp-maliyeti': { tr: 'MVP maliyeti', en: 'MVP cost' },
  nodejs: { tr: 'Node.js', en: 'Node.js' },
  observability: { tr: 'Gözlemlenebilirlik', en: 'Observability' },
  'offline-first': { tr: 'Offline-first', en: 'Offline-first' },
  'openai-integration-services': { tr: 'OpenAI entegrasyon hizmetleri', en: 'OpenAI integration services' },
  'ozel-yazilim-gelistirme': { tr: 'Özel yazılım geliştirme', en: 'Custom software development' },
  ownership: { tr: 'Kod sahipliği (ownership)', en: 'Code ownership' },
  performance: { tr: 'Performans', en: 'Performance' },
  'product-quality': { tr: 'Ürün kalitesi', en: 'Product quality' },
  'progressive-delivery': { tr: 'Progressive delivery', en: 'Progressive delivery' },
  react: { tr: 'React', en: 'React' },
  'react-native': { tr: 'React Native', en: 'React Native' },
  release: { tr: 'Release yönetimi', en: 'Release management' },
  reliability: { tr: 'Güvenilirlik', en: 'Reliability' },
  rollback: { tr: 'Rollback', en: 'Rollback' },
  saas: { tr: 'SaaS', en: 'SaaS' },
  'saas-development-company': { tr: 'SaaS geliştirme şirketi', en: 'SaaS development company' },
  'saas-development-cost': { tr: 'SaaS geliştirme maliyeti', en: 'SaaS development cost' },
  'saas-gelistirme': { tr: 'SaaS geliştirme', en: 'SaaS development' },
  'saas-mvp': { tr: 'SaaS MVP', en: 'SaaS MVP' },
  'schema-evolution': { tr: 'Şema evrimi', en: 'Schema evolution' },
  security: { tr: 'Güvenlik', en: 'Security' },
  serverless: { tr: 'Serverless', en: 'Serverless' },
  'software-development-partner': { tr: 'Yazılım geliştirme partneri', en: 'Software development partner' },
  sre: { tr: 'SRE', en: 'SRE' },
  'startup-product-engineering': { tr: 'Startup ürün mühendisliği', en: 'Startup product engineering' },
  'state-architecture': { tr: 'State mimarisi', en: 'State architecture' },
  'streaming-ui': { tr: 'Streaming arayüzler', en: 'Streaming UI' },
  suspense: { tr: 'React Suspense', en: 'React Suspense' },
  sync: { tr: 'Veri senkronizasyonu', en: 'Data sync' },
  'system-design': { tr: 'Sistem tasarımı', en: 'System design' },
  'tech-debt': { tr: 'Teknik borç', en: 'Tech debt' },
  testing: { tr: 'Test stratejisi', en: 'Testing' },
  ux: { tr: 'UX tasarımı', en: 'UX design' },
  'yazilim-sirketi': { tr: 'Yazılım şirketi', en: 'Software company' },
};

const ACRONYMS = new Set(['ai', 'api', 'llm', 'mvp', 'sre', 'jsi', 'ux', 'ui', 'ios', 'b2b', 'erp', 'crm']);

function fallbackLabel(raw) {
  const text = String(raw).trim().replace(/[-_]+/g, ' ');
  const words = text.split(/\s+/).map((word) => {
    const lower = word.toLowerCase();
    if (ACRONYMS.has(lower)) return lower.toUpperCase();
    if (lower === 'saas') return 'SaaS';
    return word;
  });
  const joined = words.join(' ');
  return joined.charAt(0).toUpperCase() + joined.slice(1);
}

function pickMostCommon(values) {
  const counts = new Map();
  for (const value of values) counts.set(value, (counts.get(value) ?? 0) + 1);
  return [...counts.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))[0]?.[0] ?? '';
}

/**
 * @param {{ slug: string; publishedAt: string; tags: string[]; locales: string[] }[]} posts
 *   summaries with raw tags; `locales` lists languages with a title and body
 * @returns {{
 *   slug: string;
 *   label: { tr: string; en: string };
 *   aliases: string[];
 *   postSlugs: string[];
 *   postCount: number;
 *   locales: string[];
 *   indexable: boolean;
 *   lastmod: string;
 * }[]} sorted by slug; post slugs newest first
 */
export function buildBlogTagPages(posts) {
  const bySlug = new Map();
  const orderedPosts = [...posts].sort(
    (a, b) => String(b.publishedAt).localeCompare(String(a.publishedAt)) || a.slug.localeCompare(b.slug),
  );
  for (const post of orderedPosts) {
    const seen = new Set();
    for (const raw of post.tags ?? []) {
      const slug = canonicalBlogTagSlug(raw);
      if (!slug || seen.has(slug)) continue;
      seen.add(slug);
      if (!bySlug.has(slug)) bySlug.set(slug, { raws: [], posts: [] });
      const entry = bySlug.get(slug);
      entry.raws.push(String(raw).trim());
      entry.posts.push(post);
    }
  }

  return [...bySlug.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([slug, { raws, posts: tagged }]) => {
      const fallback = fallbackLabel(pickMostCommon(raws));
      const preset = BLOG_TAG_LABELS[slug];
      const locales = BLOG_TAG_LOCALES.filter((locale) =>
        tagged.some((post) => (post.locales ?? BLOG_TAG_LOCALES).includes(locale)),
      );
      const countIn = (locale) =>
        tagged.filter((post) => (post.locales ?? BLOG_TAG_LOCALES).includes(locale)).length;
      return {
        slug,
        label: { tr: preset?.tr ?? fallback, en: preset?.en ?? fallback },
        aliases: [...new Set(raws.map(normalizeBlogTagSlug))].filter((alias) => alias !== slug).sort(),
        postSlugs: tagged.map((post) => post.slug),
        postCount: tagged.length,
        locales,
        indexable:
          locales.length > 0 && locales.every((locale) => countIn(locale) >= BLOG_TAG_MIN_INDEXABLE_POSTS),
        lastmod: tagged.map((post) => String(post.publishedAt).slice(0, 10)).sort().at(-1) ?? '',
      };
    });
}

/**
 * Tags sharing the most posts with `slug` (ties: larger tag first, then slug).
 * @param {string} slug
 * @param {{ slug: string; postSlugs: string[]; postCount: number }[]} pages
 */
export function relatedBlogTags(slug, pages, limit = 8) {
  const current = pages.find((page) => page.slug === slug);
  if (!current) return [];
  const own = new Set(current.postSlugs);
  return pages
    .filter((page) => page.slug !== slug)
    .map((page) => ({ page, shared: page.postSlugs.filter((post) => own.has(post)).length }))
    .filter(({ shared }) => shared > 0)
    .sort((a, b) => b.shared - a.shared || b.page.postCount - a.page.postCount || a.page.slug.localeCompare(b.page.slug))
    .slice(0, limit)
    .map(({ page, shared }) => ({ ...page, shared }));
}
